// packages/core-react/src/stores/notifications.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface NotificationData {
  id: string;
  title: string;
  body: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  isRead: boolean;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  actionUrl?: string;
  contextData?: Record<string, unknown>;
}

export interface SystemAlert {
  id: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  userStatusKey?: string;
  timestamp: Date;
  dismissed?: boolean;
}

export interface NotificationsStore {
  // User Notifications
  notifications: NotificationData[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;

  // System Alerts
  systemAlerts: SystemAlert[];

  // Actions
  addNotification: (notification: NotificationData) => void;
  updateNotification: (id: string, updates: Partial<NotificationData>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setUnreadCount: (count: number) => void;

  // System Alerts
  addSystemAlert: (alert: SystemAlert) => void;
  dismissSystemAlert: (id: string) => void;
  clearSystemAlerts: () => void;
  setSystemAlerts: (alerts: SystemAlert[]) => void;

  // Utils
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useNotificationsStore = create<NotificationsStore>()(
  persist(
    (set, _get) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      systemAlerts: [],

      // User Notifications
      addNotification: (notification: NotificationData) =>
        set(state => {
          // Check if notification already exists
          const exists = state.notifications.some(
            (n: NotificationData) => n.id === notification.id
          );
          if (exists) return state;

          const newCount = !notification.isRead ? state.unreadCount + 1 : state.unreadCount;
          return {
            ...state,
            notifications: [notification, ...state.notifications],
            unreadCount: newCount,
          };
        }),

      updateNotification: (id: string, updates: Partial<NotificationData>) =>
        set(state => {
          const notification = state.notifications.find((n: NotificationData) => n.id === id);
          if (!notification) return state;

          const wasUnread = !notification.isRead;
          const isNowRead = updates.isRead === true;

          const updatedNotifications = state.notifications.map((n: NotificationData) =>
            n.id === id ? { ...n, ...updates } : n
          );

          const newCount =
            wasUnread && isNowRead ? Math.max(0, state.unreadCount - 1) : state.unreadCount;

          return {
            ...state,
            notifications: updatedNotifications,
            unreadCount: newCount,
          };
        }),

      markAsRead: (id: string) =>
        set(state => {
          const notification = state.notifications.find((n: NotificationData) => n.id === id);
          if (!notification || notification.isRead) return state;

          return {
            ...state,
            notifications: state.notifications.map((n: NotificationData) =>
              n.id === id ? { ...n, isRead: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),

      markAllAsRead: () =>
        set(state => ({
          ...state,
          notifications: state.notifications.map((n: NotificationData) => ({
            ...n,
            isRead: true,
          })),
          unreadCount: 0,
        })),

      removeNotification: (id: string) =>
        set(state => {
          const notification = state.notifications.find((n: NotificationData) => n.id === id);
          const newCount =
            notification && !notification.isRead
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount;

          return {
            ...state,
            notifications: state.notifications.filter((n: NotificationData) => n.id !== id),
            unreadCount: newCount,
          };
        }),

      clearAll: () =>
        set(state => ({
          ...state,
          notifications: [],
          unreadCount: 0,
          systemAlerts: [],
          error: null,
        })),

      setUnreadCount: (count: number) =>
        set(state => ({
          ...state,
          unreadCount: Math.max(0, count),
        })),

      // System Alerts
      addSystemAlert: (alert: SystemAlert) =>
        set(state => {
          // Check if alert already exists
          const exists = state.systemAlerts.some((a: SystemAlert) => a.id === alert.id);
          if (exists) return state;

          return {
            ...state,
            systemAlerts: [...state.systemAlerts, alert],
          };
        }),

      dismissSystemAlert: (id: string) =>
        set(state => ({
          ...state,
          systemAlerts: state.systemAlerts.map((a: SystemAlert) =>
            a.id === id ? { ...a, dismissed: true } : a
          ),
        })),

      clearSystemAlerts: () =>
        set(state => ({
          ...state,
          systemAlerts: [],
        })),

      setSystemAlerts: (alerts: SystemAlert[]) =>
        set(state => ({
          ...state,
          systemAlerts: alerts,
        })),

      // Utils
      setLoading: (loading: boolean) =>
        set(state => ({
          ...state,
          isLoading: loading,
        })),

      setError: (error: string | null) =>
        set(state => ({
          ...state,
          error: error,
        })),
    }),
    {
      name: 'tankef-notifications',
      storage:
        typeof window !== 'undefined'
          ? {
              getItem: (name: string) => {
                const item = localStorage.getItem(name);
                return item ? JSON.parse(item) : null;
              },
              setItem: (name: string, value: unknown) => {
                localStorage.setItem(name, JSON.stringify(value));
              },
              removeItem: (name: string) => {
                localStorage.removeItem(name);
              },
            }
          : undefined,
    }
  )
);

// Selector hooks para optimizar renders
export const useNotificationList = () => useNotificationsStore(state => state.notifications);

export const useUnreadCount = () => useNotificationsStore(state => state.unreadCount);

export const useSystemAlerts = () => useNotificationsStore(state => state.systemAlerts);

export const useNotificationsActions = () =>
  useNotificationsStore(state => ({
    addNotification: state.addNotification,
    markAsRead: state.markAsRead,
    markAllAsRead: state.markAllAsRead,
    removeNotification: state.removeNotification,
    dismissSystemAlert: state.dismissSystemAlert,
  }));
